import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import {
  CHOICE_TYPES,
  FIELD_TYPES,
  getForm,
  keyFrom,
  listSubmissions,
  publishForm,
  unpublishForm,
  updateForm,
  type FieldRow,
  type FieldType,
} from './forms-api';

/**
 * Building a form, and reading what it collected.
 *
 * A list of fields rather than a canvas. A form is a sequence of questions and the only spatial
 * decision worth making is what comes first — a drag-and-drop canvas would be a lot of machinery to
 * express an ordering that two buttons express exactly as well, and it would not work on a phone.
 *
 * The whole field list is saved at once, because that is what the endpoint replaces. A builder that
 * saved one field at a time would have to reconcile its own idea of the order with the server's after
 * every keystroke, and the first thing to go wrong would be a question quietly losing its place.
 */

type Tab = 'build' | 'results';

export function FormBuilderPage(): React.JSX.Element {
  const { formId } = useParams<{ formId: string }>();
  const id = formId!;
  const client = useQueryClient();

  const [tab, setTab] = useState<Tab>('build');
  const [fields, setFields] = useState<FieldRow[]>([]);
  const [name, setName] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useQuery({ queryKey: ['form', id], queryFn: () => getForm(id) });

  useEffect(() => {
    if (!form.data) return;

    setFields([...form.data.fields].sort((a, b) => a.position - b.position));
    setName(form.data.name);
    setSuccessMessage(form.data.successMessage ?? '');
    setDirty(false);
  }, [form.data]);

  const save = useMutation({
    mutationFn: () =>
      updateForm(id, {
        name: name.trim(),
        successMessage: successMessage.trim() === '' ? null : successMessage.trim(),
        fields: fields.map((field, position) => ({
          key: field.key,
          label: field.label,
          fieldType: field.field_type,
          required: field.required,
          position,
          ...(field.help_text ? { helpText: field.help_text } : {}),
          ...(field.placeholder ? { placeholder: field.placeholder } : {}),
          ...(field.options && field.options.length > 0 ? { options: field.options } : {}),
          ...(field.maps_to ? { mapsTo: field.maps_to } : {}),
        })),
      }),
    onSuccess: () => {
      setError(null);
      setDirty(false);
      void client.invalidateQueries({ queryKey: ['form', id] });
      void client.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const status = useMutation({
    mutationFn: (next: 'PUBLISHED' | 'DRAFT') =>
      next === 'PUBLISHED' ? publishForm(id) : unpublishForm(id),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['form', id] });
      void client.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  function change(index: number, changes: Partial<FieldRow>): void {
    setDirty(true);
    setFields((current) =>
      current.map((field, position) => (position === index ? { ...field, ...changes } : field)),
    );
  }

  function move(index: number, by: number): void {
    const target = index + by;

    if (target < 0 || target >= fields.length) return;

    setDirty(true);
    setFields((current) => {
      const next = [...current];
      const [moved] = next.splice(index, 1);

      next.splice(target, 0, moved!);

      return next;
    });
  }

  function add(): void {
    setDirty(true);
    setFields((current) => [
      ...current,
      {
        key: '',
        label: '',
        field_type: 'TEXT',
        required: false,
        position: current.length,
      },
    ]);
  }

  if (form.isLoading) return <Loading />;
  if (form.error) return <ErrorState error={form.error} onRetry={() => void form.refetch()} />;

  const detail = form.data!;
  const published = detail.status === 'PUBLISHED';
  // A key is what an answer is stored under. Two fields sharing one means one of them is lost.
  const duplicateKeys = keysSeenTwice(fields);
  const incomplete = fields.some((field) => field.label.trim() === '' || field.key.trim() === '');

  return (
    <div className="view">
      <PageHeader
        title={detail.name}
        description={detail.description ?? t('forms.builderDescription')}
        actions={
          <div className="cell-inline">
            <Badge tone={published ? 'positive' : 'neutral'}>
              {t(`forms.state.${detail.status}`)}
            </Badge>

            <button
              type="button"
              className="button button--quiet button--compact"
              disabled={status.isPending || (fields.length === 0 && !published)}
              onClick={() => status.mutate(published ? 'DRAFT' : 'PUBLISHED')}
            >
              {published ? t('forms.unpublish') : t('forms.publish')}
            </button>

            <Link className="button button--quiet button--compact" to="/marketing/formularios">
              {t('forms.backToList')}
            </Link>
          </div>
        }
      />

      {detail.publicUrl && published && (
        <p className="notice">
          {t('forms.publicUrl')}: <code>{detail.publicUrl}</code>
        </p>
      )}

      <nav className="tabs" aria-label={t('nav.forms')}>
        <button
          type="button"
          className={`tab${tab === 'build' ? ' is-active' : ''}`}
          onClick={() => setTab('build')}
        >
          {t('forms.tab.build')}
        </button>
        <button
          type="button"
          className={`tab${tab === 'results' ? ' is-active' : ''}`}
          onClick={() => setTab('results')}
        >
          {t('forms.tab.results')}
        </button>
      </nav>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {tab === 'results' ? (
        <Submissions formId={id} fields={detail.fields} />
      ) : (
        <>
          <Panel title={t('forms.settings')}>
            <div className="form-grid">
              <label className="field">
                <span>{t('forms.name')}</span>
                <input
                  value={name}
                  onChange={(event) => {
                    setDirty(true);
                    setName(event.target.value);
                  }}
                />
              </label>

              <label className="field">
                <span>{t('forms.successMessage')}</span>
                <input
                  value={successMessage}
                  placeholder={t('forms.successPlaceholder')}
                  onChange={(event) => {
                    setDirty(true);
                    setSuccessMessage(event.target.value);
                  }}
                />
              </label>
            </div>
          </Panel>

          <Panel
            title={t('forms.fields')}
            actions={
              <button type="button" className="button button--quiet button--compact" onClick={add}>
                {t('forms.addField')}
              </button>
            }
          >
            {fields.length === 0 ? (
              <p className="muted">{t('forms.noFields')}</p>
            ) : (
              <ol className="builder">
                {fields.map((field, index) => (
                  <FieldEditor
                    key={`${index}-${field.id ?? 'new'}`}
                    field={field}
                    duplicate={duplicateKeys.has(field.key) && field.key !== ''}
                    first={index === 0}
                    last={index === fields.length - 1}
                    onChange={(changes) => change(index, changes)}
                    onMove={(by) => move(index, by)}
                    onRemove={() => {
                      setDirty(true);
                      setFields((current) => current.filter((_, position) => position !== index));
                    }}
                  />
                ))}
              </ol>
            )}

            <div className="cell-inline">
              <button
                type="button"
                className="button button--primary"
                disabled={save.isPending || incomplete || duplicateKeys.size > 0}
                onClick={() => save.mutate()}
              >
                {save.isPending ? t('common.saving') : t('common.save')}
              </button>

              {incomplete && <span className="muted small">{t('forms.incomplete')}</span>}
              {duplicateKeys.size > 0 && (
                <span className="muted small">{t('forms.duplicateKeys')}</span>
              )}
              {dirty && !save.isPending && (
                <span className="muted small">{t('forms.unsaved')}</span>
              )}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

function FieldEditor({
  field,
  duplicate,
  first,
  last,
  onChange,
  onMove,
  onRemove,
}: {
  field: FieldRow;
  duplicate: boolean;
  first: boolean;
  last: boolean;
  onChange: (changes: Partial<FieldRow>) => void;
  onMove: (by: number) => void;
  onRemove: () => void;
}): React.JSX.Element {
  // The key follows the label until somebody edits it, and then it stops: a key that kept changing
  // would break every answer already stored under the old one.
  const [keyTouched, setKeyTouched] = useState(field.key !== '');

  const choices = CHOICE_TYPES.includes(field.field_type);

  return (
    <li className="builder__field">
      <div className="builder__grip">
        <button
          type="button"
          className="builder__move"
          disabled={first}
          aria-label={t('forms.moveUp')}
          onClick={() => onMove(-1)}
        >
          ↑
        </button>
        <button
          type="button"
          className="builder__move"
          disabled={last}
          aria-label={t('forms.moveDown')}
          onClick={() => onMove(1)}
        >
          ↓
        </button>
      </div>

      <div className="builder__body">
        <div className="form-grid">
          <label className="field">
            <span>{t('forms.fieldLabel')}</span>
            <input
              value={field.label}
              placeholder={t('forms.fieldLabelPlaceholder')}
              onChange={(event) => {
                const label = event.target.value;

                onChange(keyTouched ? { label } : { label, key: keyFrom(label) });
              }}
            />
          </label>

          <label className="field">
            <span>{t('forms.fieldType')}</span>
            <select
              className="select"
              value={field.field_type}
              onChange={(event) => onChange({ field_type: event.target.value as FieldType })}
            >
              {FIELD_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`forms.field.${type}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>{t('forms.fieldKey')}</span>
            <input
              value={field.key}
              onChange={(event) => {
                setKeyTouched(true);
                onChange({ key: keyFrom(event.target.value) });
              }}
            />
            {duplicate && <span className="small alert">{t('forms.duplicateKey')}</span>}
          </label>
        </div>

        {choices && (
          <label className="field">
            <span>{t('forms.options')}</span>
            <textarea
              rows={3}
              value={(field.options ?? []).map((option) => option.value).join('\n')}
              placeholder={t('forms.optionsPlaceholder')}
              onChange={(event) =>
                onChange({
                  options: event.target.value
                    .split('\n')
                    .map((line) => line.trim())
                    .filter(Boolean)
                    .map((value) => ({ value })),
                })
              }
            />
          </label>
        )}

        <div className="cell-inline">
          <label className="prefs__switch">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(event) => onChange({ required: event.target.checked })}
            />
            <span className="prefs__switch-text">
              <span>{t('forms.required')}</span>
            </span>
          </label>

          <button type="button" className="button button--quiet button--compact" onClick={onRemove}>
            {t('forms.removeField')}
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * What the form collected.
 *
 * A column per field rather than a blob of JSON: the answers are the point, and the field list is
 * what says which columns exist and in what order. A submission from before a field was removed
 * simply has nothing in that column, which is the truth.
 */
function Submissions({
  formId,
  fields,
}: {
  formId: string;
  fields: FieldRow[];
}): React.JSX.Element {
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const page = useQuery({
    queryKey: ['submissions', formId, cursor],
    queryFn: () => listSubmissions(formId, cursor),
  });

  if (page.isLoading) return <Loading />;
  if (page.error) return <ErrorState error={page.error} onRetry={() => void page.refetch()} />;

  const rows = page.data?.submissions ?? [];
  const columns = [...fields].sort((a, b) => a.position - b.position).slice(0, 8);

  return (
    <Panel title={t('forms.submissions')}>
      {rows.length === 0 ? (
        <p className="muted">{t('forms.noSubmissions')}</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>{t('common.createdAt')}</th>
                {columns.map((field) => (
                  <th key={field.key}>{field.label}</th>
                ))}
                <th>{t('crm.field.status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((submission) => (
                <tr key={submission.id}>
                  <td>{formatDateTime(submission.created_at)}</td>
                  {columns.map((field) => (
                    <td key={field.key}>{answerOf(submission.data[field.key])}</td>
                  ))}
                  <td>
                    <Badge
                      tone={
                        submission.status === 'SPAM'
                          ? 'warning'
                          : submission.status === 'FAILED'
                            ? 'danger'
                            : 'positive'
                      }
                    >
                      {t(`forms.submission.${submission.status}`)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {page.data?.nextCursor && (
        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={() => setCursor(page.data.nextCursor ?? undefined)}
        >
          {t('forms.loadMore')}
        </button>
      )}
    </Panel>
  );
}

/** One answer as a cell. An array is a multi-select; an object is not something a table can show. */
function answerOf(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');
  if (typeof value === 'object') return '…';

  return String(value);
}

/** The keys that appear more than once, so the builder can refuse to save them. */
function keysSeenTwice(fields: FieldRow[]): Set<string> {
  const seen = new Set<string>();
  const twice = new Set<string>();

  for (const field of fields) {
    if (seen.has(field.key)) twice.add(field.key);
    seen.add(field.key);
  }

  return twice;
}
